import { Tag } from "antd";
import dayjs from "dayjs";
import React from "react";
import { $t } from "~/i18n.js";

import { BevoegdheidScore } from "~/components/bevoegdheden/components/BevoegdheidScore.jsx";
import { ParseMultipleDateWithPossibleError, ParseTrainingFlightDate } from "~/components/profile/parseTrainingFlightDate.jsx";
import { Phone } from "~/components/profile/phone.jsx";
import {
  getUserAvatar,
  getUserAvatarError,
} from "~/components/profile/selectPilot.jsx";
import { ReadMore } from "~/components/readmore/readMore.jsx";
import { RecencyCurrencyBarometer } from "~/components/recency/RecencyCurrencyBarometer.jsx";
import { _parseDate } from "~/lib/localize.js";
import { SortAscendingOutlined } from "@ant-design/icons";
import { KNVVLStatus } from "~/components/profile/tabs/KNVVLComponents.jsx";

const Avatar = (groups_filter, group_name_lookup = {}, history = null) => {
  
  return {
    title: $t("Lid"),
    dataIndex: "name",
    fixed: "left",
    sorter: (a, b, sortOrder) => {
      if (sortOrder === "ascend") {
        return ("" + a.first_name).localeCompare(b.first_name);
      }
      return ("" + b.last_name).localeCompare(a.last_name);
    },
    sortIcon: (d) => {
      if (d.sortOrder === "ascend") {
        return (
          <span style={{ marginLeft: 4, fontSize: "12px", fontWeight: "bold" }}>
            {$t("achternaam")}
          </span>
        );
      }
      if (d.sortOrder === "descend") {
        return (
          <span style={{ marginLeft: 4, fontSize: "12px", fontWeight: "bold" }}>
            {$t("voornaam")}
          </span>
        );
      }
      // return sort arrow icon
      return <SortAscendingOutlined />;
    },

    filters:
      groups_filter &&
      groups_filter.length > 1 &&
      groups_filter
        .split(",")
        .map((g) => ({ text: group_name_lookup[g] || g, value: g.trim() })),
    onFilter: (value, record) => record.group_keys?.includes(value),
    render: (text, vlieger) => (
      <div
        className="row"
        onClick={() =>
          setTimeout(() => history && history.push(`${history.location.pathname}/${vlieger.id}`), 0)
        }
      >
        <img
          className="avatarLedenLijst"
          alt=""
          src={getUserAvatar(vlieger, true)}
          onError={getUserAvatarError}
        />
        <div
          className="column"
          style={{ marginLeft: 12, alignItems: "flex-start" }}
        >
          <p>{vlieger.name}</p>

          <div className="Row">
            {Object.keys(group_name_lookup).length > 1 && groups_filter &&
              groups_filter.length > 1 &&
              groups_filter
                .split(",")
                .map(
                  (group, idx) =>
                    vlieger.group_keys &&
                    vlieger.group_keys?.includes(group.trim()) && (
                      <Tag key={idx}>{group_name_lookup[group]}</Tag>
                    )
                )}
          </div>
        </div>
      </div>
    ),
  };
};

const PhoneWrap = () => ({
  title: $t("Telefoon"),
  dataIndex: "phone",
  render: (text, vlieger) => (
    <Phone number={vlieger.phone} private_mode={vlieger.private_mode} />
  ),
});

const KNVVL = () => ({
  title: $t("KNVVL"),
  filters: [
    { text: $t("Geldig"), value: "valid" },
    { text: $t("Verlopen"), value: "expired" },
    { text: $t("Geen data"), value: "missing" },
  ],
  onFilter: (value, record) => {
    // if user not in basic_instructor or instructeur or zweefvlieger, show nothing
    const userGroups = record.group_keys || [];
    if (!userGroups.includes('basic_instructor') && !userGroups.includes('instructeur') && !userGroups.includes('zweefvlieger')) {
      return null;
    }
    
    if (!record.association || !record.association.end) {
      return value === "missing";
    }
    const endDate = dayjs(record.association.end);
    const isValid = endDate.isAfter(dayjs());
    if (value === "valid") return isValid;
    if (value === "expired") return !isValid;
    return false;
  },
  render: (t, record) => (
    <KNVVLStatus 
      association={record.association} 
      userGroups={record.group_keys || []} 
    />
  ),
});const CurrencyColumn = () => ({
  title: $t("Currency"),
  filters: [
    { text: $t("Groen"), value: "groen" },
    { text: $t("Geel"), value: "geel" },
    { text: $t("Oranje"), value: "orange" },
    { text: $t("Rood"), value: "rood" },
    { text: $t("Verloopt binnenkort"), value: "3m" },
    { text: $t("Check medical"), value: "check_med" },
    { text: $t("Ongeldig brevet"), value: "spl" },
    { text: $t("Ongeldig lierstart"), value: "lier" },
    { text: $t("Ongeldig sleepstart"), value: "sleep" },
    { text: $t("Ongeldig zelfstart"), value: "zelf" },
    { text: $t("Ongeldig TMG"), value: "tmg" },
    { text: $t("Ongeldig pax"), value: "pax" },
    { text: $t("Ongeldig FI(s)"), value: "fis" },
  ],
  onFilter: (value, record) => {
    if (value === "check_med") {
      return record.check_med;
    }
    if (value === "spl") {
      return record.recency && record.recency.spl === "invalid";
    }
    if (value === "lier") {
      return record.recency && record.recency.lieraantekening === "invalid";
    }
    if (value === "sleep") {
      return record.recency && record.recency.sleepaantekening === "invalid";
    }
    if (value === "zelf") {
      return (
        record.recency && record.recency.zelfstartaantekening === "invalid"
      );
    }
    if (value === "tmg") {
      return record.recency && record.recency.tmgaantekening === "invalid";
    }
    if (value === "pax") {
      return record.recency && record.recency.passagiervliegen === "invalid";
    }
    if (value === "fis") {
      return record.recency && record.recency.fis === "invalid";
    }
    if (value === "3m" && record.recency) {
      // show users that have a recency key that stops
      const now = dayjs();
      return Object.keys(record.recency).some((key) => {
        if (!key.endsWith("pax_valid_to") && key.endsWith("valid_to")) {
          if (dayjs(record.recency[key], "YYYY-MM-DD").diff(now, "days") < 90) {
            return true;
          }
        }
      });
    }
    return value === record.currency;
  },
  render: (text, vlieger, index) =>
    vlieger.currency ? <RecencyCurrencyBarometer pilot={vlieger} /> : null,
});


const Check = () => ({
  title: $t("Trainingsvlucht"),
  dataIndex: "last_check_start",
  filters: [{text: $t('Verloopt binnenkort'), value: 1}],
  onFilter: (value, record) => {
    if (value === 1) {
      return record.last_check_start && record.last_check_start.split(",").some((d) => {
        return (new Date() - new Date(d)) > 600 * 1000 * 60 * 60 * 24
      });
    }
    return true;
  },
    
  sorter: (a, b) => {
    let an = a?.last_check_start?.split(",")[0]
    if (!an) return -1;
    let bn = b?.last_check_start?.split(",")[0]
    if (!bn) return 1;
    return new Date(an) - new Date(bn)
  },
  render: (text) => (
    <ParseTrainingFlightDate last_check_start={text} hideOnbekendTag={true} />
  ),
});

export const NotitieColumn = (notities) => ({
  title: $t("Laatste notitie"),
  sorter: (a, b) => {
    let an = notities[a.id];
    if (!an) return -1;
    let bn = notities[b.id];
    if (!bn) return 1;
    return new Date(an.datum) - new Date(bn.datum);
  },
  render: (_, vlieger) => {
    const v = notities && notities[vlieger.id];
    if (!v) return null;
    return <ReadMore text={v.bericht} maxLength={50} />;
  },
});

export const LatestBehaald = (latestBehaaldeBevoegdheden) => ({
  title: $t("Laatst behaald"),
  sorter: (a, b) => {
    let an = latestBehaaldeBevoegdheden[a.id];
    if (!an) return -1;
    let bn = latestBehaaldeBevoegdheden[b.id];
    if (!bn) return 1;
    return new Date(an.date_assigned) - new Date(bn.date_assigned);
  },
  render: (_, vlieger) => {
    const b = latestBehaaldeBevoegdheden[vlieger.id];
    if (b) {
      return (
          <div className="row" style={{gap: 6}}>
          <div style={{width: 18}}>
          <BevoegdheidScore score={b.score} />
          </div>
          <p>
            <span>{b.bevoegdheid_name}</span>
            <br/>
            <span className="small gray">{_parseDate(b.date_assigned)}</span>
          </p>
        </div>
      );
    }
  },
});

const Dbo = () => ({
  title: $t("DBO starts"),
  dataIndex: "dbo",
  sorter: (a, b) => a.dbo - b.dbo,
});

const Date_first_flight = () => ({
  title: $t("Eerste vlucht"),
  dataIndex: "date_first_flight",
  render: (text) => _parseDate(text),
  sorter: (a, b) =>
    ("" + a.date_first_flight).localeCompare(b.date_first_flight),
});

const Solo = () => ({
  title: $t("Solo starts"),
  dataIndex: "pic",
  sorter: (a, b) => a.pic - b.pic,
});

// sort value must return 0 because you can't compare undefined
const get_value = (vlieger, clusterName, data) => {
  if (!data) return 0
  let ob = data[vlieger.id + "_" + clusterName]
  if (!ob) return 0
  return ob['count']
}
 
const ShowTrainingData = ({ vlieger, cluster, data }) => {
  if (!data) return null
  const getKey = vlieger.id + "_" + cluster.name;
  const ob = data[getKey]
  if (!ob) return null
  return <p>
      {ob['count'] || 0} {ob['minDate'] && <ParseMultipleDateWithPossibleError dates={ob['minDate']} checkDateInPast={false} maxDays={90}/>}
  </p>
};
  

export const clusterColumn = (
  cluster,
  behaalde_bevoegdheden,
  active_dto_trainingen
) => ({
  title: cluster.name,
  sorter: (a, b) =>
    get_value(a, cluster.name, behaalde_bevoegdheden) -
    get_value(b, cluster.name, behaalde_bevoegdheden),
  filters: [{text: $t('In programma'), value: 1},
            {text: $t('Verloopt binnenkort'), value: 2}],
  onFilter: (value, record) => {

    const getKey = record.id + "_" + cluster.name;
    const ob = behaalde_bevoegdheden[getKey]
    if (!ob) return false
    
    if (value === 1) {
      return ob["count"] > 0;
    }

    if (value === 2) {
      return ob["minDate"] && (new Date(ob["minDate"]) - new Date()) < 90 * 1000 * 60 * 60 * 24 * 10
  
    }

    return false;
  },
    
  
  render: (text, vlieger, index) => {
    const status =
      active_dto_trainingen &&
      active_dto_trainingen[vlieger.id + "_" + cluster.id]
        ? active_dto_trainingen[vlieger.id + "_" + cluster.id]
        : null;
    return (
      status && (
        <>
          {status === "started" && (
            <ShowTrainingData
              vlieger={vlieger}
              cluster={cluster}
              data={behaalde_bevoegdheden || {}}
            />
          )}
          {status === "completed" && <Tag color="gold">{$t("afgerond")}</Tag>}
          {status === "discontinued" && <Tag>{$t("gestaakt")}</Tag>}
        </>
      )
    );
  },
});

export const ledenLijst = (
  groups_filter,
  group_name_lookup,
  history,
  hasFlights = True
) =>
  [
    Avatar(groups_filter, group_name_lookup, history),
    hasFlights && CurrencyColumn(),
    PhoneWrap(),
  ].filter((f) => f);

export const InstructeurLijst = (
  notities,
  groups_filter,
  group_name_lookup,
  history,
  hasFlights = true,
  hasKNVVL = true
) =>
  [
    Avatar(groups_filter, group_name_lookup, history),
    hasFlights && CurrencyColumn(),
    PhoneWrap(),
    hasKNVVL && KNVVL(),
    hasFlights && Check(),
    NotitieColumn(notities),
  ].filter((f) => f);
window.InstructeurLijst = InstructeurLijst;

export const DTOlijst = (
  groups_filter,
  group_name_lookup,
  latestBehaaldeBevoegdheden,
  history,
  hasFlights = true
) =>
  [
    Avatar(groups_filter, group_name_lookup, history),
    hasFlights && CurrencyColumn(),
    hasFlights && Dbo(),
    hasFlights && Solo(),
    hasFlights && Date_first_flight(),
    hasFlights && {
      title: $t("Laatste vlucht"),
      dataIndex: "date_last_flight",
      render: (text) => _parseDate(text),
      sorter: (a, b) =>
        ("" + a.date_last_flight).localeCompare(b.date_last_flight),
    },
    LatestBehaald(latestBehaaldeBevoegdheden),
  ].filter((f) => f);
